import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/api/client";
import {
  Sparkles, Zap, TrendingUp, ShoppingBag, Globe, Radar, RefreshCw, Twitter, ArrowRight,
} from "lucide-react";
import BannerAds from "@/components/BannerAds";

const FEATURES = [
  { icon: Zap, title: "Autonomous content", body: "GPT-5.6 Terra writes SEO-optimized articles, reviews, and comparison tables — with affiliate links baked in automatically." },
  { icon: ShoppingBag, title: "Live Amazon PA-API", body: "Real product prices, real images, real ASINs. Never a made-up spec sheet." },
  { icon: Globe, title: "Multilingual publishing", body: "Publish the same article in 8 languages in parallel. Reach every market you already have an audience in." },
  { icon: Radar, title: "Refresh Radar", body: "Every article gets a live health score. Bulk-refresh the ones losing rank with one click." },
  { icon: TrendingUp, title: "Live sync + analytics", body: "Real click tracking, per-network breakdowns, and weekly digest emails that show what's converting." },
  { icon: Twitter, title: "Auto-share to X", body: "AI composes a scroll-stopping tweet for every article and posts it the moment you publish." },
];

export default function LandingPage() {
  const [stats, setStats] = useState({});
  const [recent, setRecent] = useState([]);
  const [picks, setPicks] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, r, p] = await Promise.all([
          api.get("/public/stats"),
          api.get("/public/articles?limit=3"),
          api.get("/public/editors-picks?limit=6"),
        ]);
        setStats(s.data);
        setRecent(r.data.items || []);
        setPicks(p.data.items || []);
      } catch {}
    })();
  }, []);

  return (
    <div className="fade-up">
      {/* Hero */}
      <section className="border-b border-slate-800">
        <div className="max-w-[1400px] mx-auto px-6 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-3">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-sky-400 mb-4">
              — Affiliate marketing · Built by AI
            </p>
            <h1 className="font-serif text-5xl lg:text-7xl leading-[1.05] text-sky-100 tracking-tight">
              List Your Business.{" "}
              <span className="italic text-sky-400/90">Get Found. Grow More.</span>
            </h1>
            <p className="mt-6 text-neutral-400 text-lg max-w-2xl leading-relaxed">
              An AI terminal that detects your affiliate network, picks a profitable niche, writes optimized content in 8 languages, publishes it, tweets it, and refreshes it monthly — all from one credential.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/signup"
                data-testid="hero-cta-signup"
                className="bg-sky-400 hover:bg-sky-300 text-slate-950 px-6 py-3.5 rounded-md font-medium flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4" /> Start free trial
              </Link>
              <Link
                to="/browse"
                data-testid="hero-cta-browse"
                className="border border-sky-400 text-sky-100 hover:bg-sky-400 hover:text-slate-950 px-6 py-3.5 rounded-md flex items-center gap-2 transition"
              >
                Browse articles <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <p className="mt-6 text-xs text-neutral-500 font-mono">
              No credit card · GPT-5.6 Terra + Gemini Nano Banana · Cancel anytime
            </p>

            {/* Filter chips — jump straight into browse pre-filtered */}
            {(stats.top_niches?.length > 0 || stats.top_languages?.length > 0) && (
              <div className="mt-10" data-testid="hero-chips">
                <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-3">
                  Or jump into a niche →
                </p>
                <div className="flex flex-wrap gap-2">
                  {(stats.top_niches || []).slice(0, 6).map((n) => (
                    <Link
                      key={n.name}
                      to={`/browse?niche=${encodeURIComponent(n.name)}`}
                      data-testid={`chip-niche-${n.name.replace(/\s+/g,"-").toLowerCase()}`}
                      className="text-xs px-3 py-1.5 rounded-full border border-sky-400/30 text-sky-200 bg-sky-500/5 hover:bg-sky-400 hover:text-slate-950 transition font-mono"
                    >
                      {n.name} <span className="text-sky-400/60">· {n.count}</span>
                    </Link>
                  ))}
                  {(stats.top_languages || []).slice(0, 4).map((l) => (
                    <Link
                      key={l}
                      to={`/browse?language=${encodeURIComponent(l)}`}
                      data-testid={`chip-lang-${l.toLowerCase()}`}
                      className="text-xs px-3 py-1.5 rounded-full border border-slate-800 text-neutral-400 hover:border-sky-400 hover:text-sky-200 transition font-mono"
                    >
                      {l}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Ticker card */}
          <div className="lg:col-span-2 border border-sky-500/25 rounded-xl p-6 bg-gradient-to-br from-sky-500/8 via-slate-900/60 to-transparent">
            <p className="font-mono text-[10px] uppercase tracking-widest text-sky-400 mb-4">— Live platform stats</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="font-serif text-4xl font-numeric text-sky-100">{stats.public_articles ?? 0}</p>
                <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mt-1">Public articles</p>
              </div>
              <div>
                <p className="font-serif text-4xl font-numeric text-sky-100">{stats.total_users ?? 0}</p>
                <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mt-1">Creators</p>
              </div>
              <div>
                <p className="font-serif text-4xl font-numeric text-sky-100">{stats.total_content_generated ?? 0}</p>
                <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mt-1">Total generated</p>
              </div>
              <div>
                <p className="font-serif text-4xl font-numeric text-sky-100">{stats.niches_covered ?? 0}</p>
                <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mt-1">Niches</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Banner Ads — leaderboard slots */}
      <BannerAds />

      {/* Editor's Picks — big hero row of admin-starred flagship articles */}
      {picks.length > 0 && (
        <section className="border-b border-slate-800 bg-gradient-to-b from-sky-500/5 to-transparent">
          <div className="max-w-[1400px] mx-auto px-6 py-20">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-sky-400 mb-3 flex items-center gap-2">
                  <Sparkles className="w-3 h-3" /> — Editor's picks
                </p>
                <h2 className="font-serif text-4xl lg:text-5xl text-sky-100">Hand-picked reads</h2>
              </div>
              <Link to="/browse" className="text-sm text-sky-300 hover:text-sky-100 flex items-center gap-1">
                See all articles <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            {/* Featured (first pick) + smaller sibling grid */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              {/* Feature card — 3/5 wide */}
              <Link
                to={`/read/${picks[0].slug || picks[0].id}`}
                data-testid={`pick-hero-${picks[0].id}`}
                className="lg:col-span-3 border border-sky-500/25 rounded-xl overflow-hidden hover:border-sky-400 transition group bg-slate-900/40"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 h-full">
                  {picks[0].hero_image_url ? (
                    <img
                      src={picks[0].hero_image_url.startsWith("http") ? picks[0].hero_image_url : `${process.env.REACT_APP_BACKEND_URL}${picks[0].hero_image_url}`}
                      alt={picks[0].title}
                      className="w-full h-full object-cover min-h-[280px]"
                    />
                  ) : (
                    <div className="w-full h-full min-h-[280px] bg-gradient-to-br from-sky-500/30 to-slate-900" />
                  )}
                  <div className="p-8 flex flex-col">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-sky-400 mb-3">{picks[0].niche}</p>
                    <h3 className="font-serif text-2xl lg:text-3xl text-sky-100 group-hover:text-sky-300 transition leading-tight flex-1">{picks[0].title}</h3>
                    <div className="mt-6 pt-6 border-t border-slate-800 flex items-center justify-between text-[11px] font-mono text-neutral-500">
                      <span>by {picks[0].author_name}</span>
                      <span>{picks[0].read_time}m · {picks[0].language || "EN"}</span>
                    </div>
                  </div>
                </div>
              </Link>

              {/* Sibling picks — 2/5 wide, stacked */}
              <div className="lg:col-span-2 grid grid-cols-1 gap-4">
                {picks.slice(1, 4).map((p) => (
                  <Link
                    key={p.id}
                    to={`/read/${p.slug || p.id}`}
                    data-testid={`pick-side-${p.id}`}
                    className="border border-slate-800 rounded-xl overflow-hidden hover:border-sky-400/40 transition group flex"
                  >
                    {p.hero_image_url ? (
                      <img
                        src={p.hero_image_url.startsWith("http") ? p.hero_image_url : `${process.env.REACT_APP_BACKEND_URL}${p.hero_image_url}`}
                        alt={p.title}
                        className="w-32 h-full object-cover flex-shrink-0 min-h-[100px]"
                      />
                    ) : (
                      <div className="w-32 flex-shrink-0 bg-gradient-to-br from-sky-500/20 to-slate-900" />
                    )}
                    <div className="p-4 flex-1 min-w-0 flex flex-col">
                      <p className="font-mono text-[10px] uppercase tracking-widest text-sky-400 truncate">{p.niche}</p>
                      <h4 className="font-serif text-base text-sky-100 group-hover:text-sky-300 mt-1 line-clamp-2 leading-snug flex-1">{p.title}</h4>
                      <p className="text-[10px] font-mono text-neutral-500 mt-2">{p.read_time}m · {p.author_name}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Extra picks in a bottom row when there are more than 4 */}
            {picks.length > 4 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                {picks.slice(4).map((p) => (
                  <Link
                    key={p.id}
                    to={`/read/${p.slug || p.id}`}
                    data-testid={`pick-extra-${p.id}`}
                    className="border border-slate-800 rounded-xl p-4 hover:border-sky-400/40 transition group flex items-center gap-4"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-[10px] uppercase tracking-widest text-sky-400 truncate">{p.niche}</p>
                      <h4 className="font-serif text-base text-sky-100 group-hover:text-sky-300 mt-1 line-clamp-2 leading-snug">{p.title}</h4>
                    </div>
                    <ArrowRight className="w-4 h-4 text-neutral-500 group-hover:text-sky-300 flex-shrink-0" />
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Features */}
      <section className="border-b border-slate-800">
        <div className="max-w-[1400px] mx-auto px-6 py-20">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-sky-400 mb-3">— What's inside</p>
          <h2 className="font-serif text-4xl lg:text-5xl text-sky-100 max-w-3xl">
            Every tool an affiliate marketer needs, powered by AI you'd otherwise pay $200/mo for.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} data-testid={`feature-${i}`} className="border border-slate-800 rounded-xl p-6 hover:border-sky-400/40 transition group">
                  <Icon className="w-5 h-5 text-sky-400 mb-4 group-hover:scale-110 transition" strokeWidth={1.5} />
                  <h3 className="font-serif text-xl text-sky-100 mb-2">{f.title}</h3>
                  <p className="text-sm text-neutral-400 leading-relaxed">{f.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Recent articles — hide any already featured as Editor's Picks */}
      {(() => {
        const pickIds = new Set(picks.map((p) => p.id));
        const filteredRecent = recent.filter((a) => !pickIds.has(a.id));
        if (filteredRecent.length === 0) return null;
        return (
        <section className="border-b border-slate-800">
          <div className="max-w-[1400px] mx-auto px-6 py-20">
            <div className="flex items-end justify-between mb-8">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-sky-400 mb-3">— Fresh off the terminal</p>
                <h2 className="font-serif text-4xl text-sky-100">Latest published articles</h2>
              </div>
              <Link to="/browse" className="text-sm text-sky-300 hover:text-sky-100 flex items-center gap-1">
                View all <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {filteredRecent.map((a) => (
                <Link
                  key={a.id}
                  to={`/read/${a.slug || a.id}`}
                  data-testid={`landing-recent-${a.id}`}
                  className="border border-slate-800 rounded-xl overflow-hidden hover:border-sky-400/40 transition group"
                >
                  {a.hero_image_url ? (
                    <img src={a.hero_image_url.startsWith("http") ? a.hero_image_url : `${process.env.REACT_APP_BACKEND_URL}${a.hero_image_url}`} alt={a.title} className="w-full h-40 object-cover" />
                  ) : (
                    <div className="w-full h-40 bg-gradient-to-br from-sky-500/20 to-slate-900" />
                  )}
                  <div className="p-5">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-sky-400 mb-2">{a.niche}</p>
                    <h3 className="font-serif text-lg text-sky-100 group-hover:text-sky-300 transition line-clamp-2 leading-snug">{a.title}</h3>
                    <p className="text-[11px] font-mono text-neutral-500 mt-3">by {a.author_name} · {a.read_time}m</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
        );
      })()}

      {/* CTA */}
      <section>
        <div className="max-w-[1400px] mx-auto px-6 py-24 text-center">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-sky-400 mb-3">— Ready to launch?</p>
          <h2 className="font-serif text-5xl lg:text-6xl text-sky-100 leading-tight max-w-3xl mx-auto">
            One credential. <span className="italic text-sky-400">One click.</span> A revenue engine.
          </h2>
          <p className="mt-6 text-neutral-400 max-w-xl mx-auto">
            Sign up in 30 seconds. Detect your network in 3. Publish your first article in 90.
          </p>
          <Link
            to="/signup"
            data-testid="cta-signup-bottom"
            className="mt-8 inline-flex items-center gap-2 bg-sky-400 hover:bg-sky-300 text-slate-950 px-8 py-4 rounded-md font-medium text-lg"
          >
            <Sparkles className="w-5 h-5" /> Start free trial
          </Link>
        </div>
      </section>

      {/* Newsletter */}
      <NewsletterSignup />
    </div>
  );
}

function NewsletterSignup() {
  const [email, setEmail] = React.useState("");
  const [status, setStatus] = React.useState("idle"); // idle | submitting | success | error
  const [msg, setMsg] = React.useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !/.+@.+\..+/.test(email)) {
      setStatus("error"); setMsg("Please enter a valid email."); return;
    }
    setStatus("submitting");
    try {
      const api = (await import("@/api/client")).default;
      const { data } = await api.post("/newsletter/subscribe", { email });
      setStatus("success");
      setMsg(data.already ? "You're already subscribed — thanks!" :
             data.resubscribed ? "Welcome back! You're re-subscribed." :
             "Subscribed! We'll email you when a new article drops.");
      setEmail("");
    } catch (err) {
      setStatus("error");
      const detail = err.response?.data?.detail;
      setMsg(typeof detail === "string" ? detail : "Could not subscribe. Try again shortly.");
    }
  };

  return (
    <section className="border-t border-slate-800">
      <div className="max-w-2xl mx-auto px-6 py-16 text-center" data-testid="newsletter-signup">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-sky-400 mb-3">— The Newsletter</p>
        <h2 className="font-serif text-3xl lg:text-4xl text-sky-100">Fresh articles in your inbox</h2>
        <p className="mt-3 text-sm text-neutral-400">
          Once we publish a new article, you'll get it before anyone else. No spam. Unsubscribe with one click.
        </p>
        <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
          <input
            data-testid="newsletter-input-email"
            type="email"
            required
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={status === "submitting"}
            className="flex-1 bg-slate-900 border border-slate-800 rounded px-4 py-3 text-sky-50 outline-none focus:border-sky-400"
          />
          <button
            data-testid="btn-newsletter-subscribe"
            type="submit"
            disabled={status === "submitting"}
            className="px-6 py-3 bg-sky-400 hover:bg-sky-300 text-slate-950 rounded font-medium disabled:opacity-60"
          >
            {status === "submitting" ? "Subscribing…" : "Subscribe"}
          </button>
        </form>
        {msg && (
          <p
            data-testid="newsletter-msg"
            className={`mt-4 text-sm font-mono ${status === "success" ? "text-emerald-400" : "text-red-400"}`}
          >
            {msg}
          </p>
        )}
      </div>
    </section>
  );
}
